import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import { BASE_IMG_URL, livePhotoImage, photographyImage } from '@images/imageUrls';
import classNames from 'classnames';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { BaseComponent } from '@components/Base/h5/BaseComponent';
import type { BaseRouteComponentProps } from '../../../types/base';
import { formatDateTime } from '@utils/timeUtils';
import { formatNumberToWan } from '@utils/formatNumber';
import InfiniteScrollList from '@components/InfiniteScrollList';
import { highlightname } from '@utils/high-light-name';
import Avatar from '@components/avatar';
import Toast from '@components/Toast';
import updateImageListSrc from '@utils/update-image-list-src';
import { px2rem } from '@utils/px2rem';

const LIVE_ICON_URL = livePhotoImage.liveIcon;

// 定义组件自身的 props
interface OwnProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    data: any;   // 必需的数据
    type?: any;  // 类型 1：正常作品列表 2：我的作品
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    emptyText?: string; // 为空时提示文字
    emptyIcon?: string; // 为空时icon
    emptyBoxStyle?: any; // 为空状态时的自定义样式
    searchKeyword?: any; // 搜索关键词，用于高亮显示
    currentItemClick?: Function;   // 点击打开详情
    voteClick?: Function;   // 点击投票
    photographyDetail?: any // 摄影大赛详情
    canvassForVotesClick?: Function;   // 点击为TA拉票
    manageCurrentWork?: Function;   // 作品详情右上角点击管理事件
    closeDetailTrigger?: number; // 触发关闭详情的标记（值变化时触发关闭）
}

// 合并路由 props
type H5WaterfallFlowProps = OwnProps & RouteComponentProps;

interface H5WaterfallFlowState {
    loading?: boolean;
    error?: any;
    pageLoaded?: any;
    currentImgDetail: any;  // 当前点击的图片数据
    // currentImgDetailShowStatus: boolean // 当前点击的图片信息弹框是否显示
}

@inject('app', 'site', 'user')
@observer
class H5WaterfallFlow extends React.Component<H5WaterfallFlowProps, H5WaterfallFlowState> {
    private readonly ITEM_WIDTH = 343;
    private readonly CARD_INFO_HEIGHT = 132;

    constructor(props: H5WaterfallFlowProps) {
        super(props);
        this.state = {
            currentImgDetail: {},   // 当前点击的图片详情数据
            // currentImgDetailShowStatus: false,  // 当前点击的图片信息弹框是否显示
        };
    }

    getEstimatedItemHeight = (item: any) => {
        const imageWidth = Number(item?.width);
        const imageHeight = Number(item?.height);

        if (imageWidth > 0 && imageHeight > 0) {
            const ratio = imageWidth / imageHeight;
            const minRatio = 9 / 16; // 0.5625
            const maxRatio = 4 / 3;  // 1.333...
            
            // 比例小于 9:16，使用固定高度 610px
            if (ratio < minRatio) {
                return 610 + this.CARD_INFO_HEIGHT;
            }
            
            // 比例大于 4:3，使用固定高度 258px
            if (ratio > maxRatio) {
                return 258 + this.CARD_INFO_HEIGHT;
            }
            
            // 正常比例范围内，按原比例计算高度
            return Math.round((this.ITEM_WIDTH / imageWidth) * imageHeight) + this.CARD_INFO_HEIGHT;
        }

        return 300 + this.CARD_INFO_HEIGHT;
    }

    componentDidMount() {
        // 可以在这里添加初始化逻辑
        console.log('这里是H5');
    }

    componentDidUpdate(prevProps: H5WaterfallFlowProps) {
        // // 当 closeDetailTrigger 变化时，关闭图片详情弹框
        // if (prevProps.closeDetailTrigger !== this.props.closeDetailTrigger) {
        //     this.setState({
        //         currentImgDetail: {},
        //         currentImgDetailShowStatus: false
        //     });
        // }
    }

    // 判断图片是否需要固定高度（超出比例范围）
    isFixedHeightImage = (item: any) => {
        const width = Number(item?.width);
        const height = Number(item?.height);
        
        if (!width || !height) {
            return false;
        }
        
        const ratio = width / height;
        const minRatio = 9 / 16; // 0.5625 (9:16)
        const maxRatio = 4 / 3;  // 1.333... (4:3)
        
        // 超出比例范围需要固定高度
        return ratio < minRatio || ratio > maxRatio;
    }

    // 获取图片容器高度
    getImageBoxHeight = (item: any) => {
        const width = Number(item?.width);
        const height = Number(item?.height);
        
        if (!width || !height) {
            return null;
        }
        
        const ratio = width / height;
        const minRatio = 9 / 16;
        const maxRatio = 4 / 3;
        
        // 比例小于 9:16（图片很窄很高），最大高度 610px
        if (ratio < minRatio) {
            return px2rem(610);
        }
        
        // 比例大于 4:3（图片很宽很矮），最小高度 258px
        if (ratio > maxRatio) {
            return px2rem(258);
        }
        
        // 正常比例范围内，不固定高度
        return null;
    }

    // 渲染瀑布流项 - 根据提供的数据结构
    renderWaterfallItem = (item: any, index: number) => {
        let { searchKeyword, type, voteClick, photographyDetail, currentItemClick, user } = this.props;
        const isLiveWork = String(item?.type || '').toLowerCase() === 'live';
        const isVideoWork = String(item?.type || '').toLowerCase() === 'video';
        
        // 判断是否需要固定高度
        const isFixedHeight = this.isFixedHeightImage(item);
        const fixedHeight = this.getImageBoxHeight(item);
        
        // 容器样式
        const imgBoxStyle: any = {};
        if (isFixedHeight && fixedHeight) {
            imgBoxStyle.height = fixedHeight;
        }
        
        return (
            <div key={item.id || index} className={styles.waterfallItem}>
                <div className={styles.waterfallCard}>
                    {item.image && (
                        <div 
                            className={classNames(styles.waterfallImgBox, isFixedHeight && styles.fixedHeightImgBox)} 
                            style={imgBoxStyle}
                            onClick={() => {
                                this.setState({
                                    currentImgDetail: item,
                                })
                                currentItemClick && currentItemClick(item)
                            }}
                        >
                            <img
                                src={updateImageListSrc(item.image, 11)}
                                alt={item.name}
                                className={classNames(styles.waterfallImg, isFixedHeight && styles.coverImg)}
                                onError={(e) => {
                                    e.currentTarget.src = `${BASE_IMG_URL}/common/pic_place1.png`;
                                }}
                            />
                            {isLiveWork && <img src={LIVE_ICON_URL} alt="" className={styles.liveTagIcon} />}
                            {isVideoWork && <img src={photographyImage.videoIcon} alt="视频作品" className={styles.videoTagIcon} draggable={false} />}
                            {/* status 为0 待审核，1 审核通过，2 审核未通过，3 下架  */}
                            {item.status == 1 && <div className={styles.waterfallVoteCount}>{item.voteCount}票</div>}
                            {item.status == 0 && <div className={classNames(styles.waterfallStatus)}>
                                <img 
                                    src={`${BASE_IMG_URL}/photography/icon1.png`} 
                                    alt="审核中"
                                    className={styles.icon}
                                />审核中
                            </div>}
                            {item.status == 2 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                                <img 
                                    src={`${BASE_IMG_URL}/photography/icon2.png`} 
                                    alt="审核中"
                                    className={styles.icon}
                                />
                                审核未通过
                            </div>}
                            {item.status == 3 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                                <img 
                                    src={`${BASE_IMG_URL}/photography/icon2.png`} 
                                    alt="审核中"
                                    className={styles.icon}
                                />
                                已下架
                                
                            </div>}
                        </div>
                    )}
                    
                    <div className={styles.waterfallInfo}>
                        <div className={styles.numBox}>
                            {(type == 2 && item.status == 1) && <div className={styles.waterfallRanking}>第{item.ranking > 100 ? '100+' : item.ranking}名</div>}
                            <div className={styles.waterfallNumber}>ID:{item.number}</div>
                        </div>
                        
                        <div className={styles.waterfallName} 
                            dangerouslySetInnerHTML={{
                                __html: searchKeyword 
                                    ? highlightname(item.name, searchKeyword) 
                                    : item.name
                            }}
                        />
                        <div className={styles.waterfallUserInfo}>
                            <div className={styles.userInfo}>
                                <Avatar
                                    avatarUrl={item.user?.avatar}
                                    userCode={item.user?.code}
                                    size={32}
                                    titleMark={item.user?.titleMark}
                                    level={item.user?.level}
                                    isOpenBlank={true}
                                ></Avatar>
                                <span className={styles.userName}>{item.user?.nickname}</span>
                            </div>
                            {(photographyDetail.status == 1 && item.user?.code == user?.userInfo?.code) && <div className={classNames(styles.voteBtn)} onClick={() => {
                                this.setState({
                                    currentImgDetail: item
                                }, () => {
                                    this.canvassForVotes()
                                })
                                
                                }}>拉票</div>}
                            {(photographyDetail.status == 1 && type !=2 && item.user?.code != user?.userInfo?.code) && <div className={classNames(styles.voteBtn, item.isVoted ? styles.votedBtn : '')} onClick={() => voteClick && voteClick(item)}>{item.isVoted ? '已投' : '投票'}</div>}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 点击拉票
    canvassForVotes = () => {
        const { photographyDetail, canvassForVotesClick } = this.props;
        let { currentImgDetail } = this.state;
        if(photographyDetail.status == 2) { // 如果已结束
            Toast.info('活动已结束')
        } else {
            canvassForVotesClick && canvassForVotesClick(currentImgDetail);
        }
    }

    renderContent() {
        const { 
            data = [],  // 提供默认空数组，避免 undefined
            loading = false, 
            hasMore = true, 
            emptyText, 
            emptyIcon, 
            emptyBoxStyle, 
            onLoadMore,
            photographyDetail,
            type,
            user,
            manageCurrentWork
        } = this.props;
        // let { currentImgDetail, currentImgDetailShowStatus } = this.state;
        
        if (!Array.isArray(data)) return null;
        
        // 如果是空数据，显示空状态
        if (data.length === 0) {
            return (
                <InfiniteScrollList
                    variant="h5"
                    data={[]}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={onLoadMore || (() => {})}
                    renderItem={() => null}
                    emptyText={emptyText || '暂无数据'}
                    emptyIcon={emptyIcon || `${BASE_IMG_URL}/common/null.png`}
                    emptyBoxStyle={emptyBoxStyle}
                />
            );
        }
        
        // 瀑布流布局：根据预估高度动态分配到较短的列
        const leftColumn: any[] = [];
        const rightColumn: any[] = [];
        let leftHeight = 0;
        let rightHeight = 0;

        data.forEach((item, index) => {
            const estimatedHeight = this.getEstimatedItemHeight(item);

            // 分配到较短的列
            if (leftHeight <= rightHeight) {
                leftColumn.push({ ...item, originalIndex: index });
                leftHeight += estimatedHeight;
            } else {
                rightColumn.push({ ...item, originalIndex: index });
                rightHeight += estimatedHeight;
            }
        });

        return (
            <div className={styles.waterfallContainer}>
                <div className={styles.waterfallColumns}>
                    <div className={styles.waterfallLeftColumn}>
                        {leftColumn.map((item) => this.renderWaterfallItem(item, item.originalIndex))}
                    </div>
                    <div className={styles.waterfallRightColumn}>
                        {rightColumn.map((item) => this.renderWaterfallItem(item, item.originalIndex))}
                    </div>
                </div>

                {/* 点击某个item，打开详情 */}
                {/* {currentImgDetailShowStatus && <div className={styles.waterfallCardDetail}>
                    <div className={styles.detailTiTleBox}>
                        <img src={`${BASE_IMG_URL}/photography/back.png`} alt="" className={styles.detailTiTleBack}  onClick={() => {
                            this.setState({
                                currentImgDetail: {},
                                currentImgDetailShowStatus: false
                            })
                        }}/>
                        <div className={styles.detailTiTle}>{currentImgDetail.name}</div>
                        {(currentImgDetail.user?.code == user?.userInfo?.code) && <img src={`${BASE_IMG_URL}/photography/icon8.png`} alt="" className={styles.detailTiTleBack}  onClick={() => {
                            manageCurrentWork && manageCurrentWork(currentImgDetail)
                        }} />}
                    </div>
                    <div className={styles.detailImgBox}>
                        <img src={currentImgDetail.image} alt="" className={styles.detailImg}/>
                        {currentImgDetail.aigcStatus == 1 && <div className={styles.aiTip}>图片属于AI生成</div>}
                        {currentImgDetail.aigcStatus == 2 && <div className={styles.aiTip}>作者声明：图片由AI生成</div>}
                        {currentImgDetail.aigcStatus == 3 && <div className={styles.aiTip}>图片疑似使用AI生成技术，请谨慎甄别</div>}
                    </div>
                    <div className={styles.detailInfoBox}>
                        <div className={styles.userInfo}>
                            {currentImgDetail.user?.avatar && (<>
                                <Avatar
                                    avatarUrl={currentImgDetail.user?.avatar}
                                    userCode={currentImgDetail.user?.code}
                                    size={60}
                                    titleMark={currentImgDetail.user?.titleMark}
                                    officialSize={24}
                                ></Avatar>
                            </>)}
                            <div className={styles.userNameBox}>
                                <span className={styles.nickname}>{currentImgDetail.user?.nickname}</span>
                                <span className={styles.titleName}>{currentImgDetail.user?.titleName}</span>
                            </div>
                        </div> 
                        <div className={styles.numBox}>
                            {currentImgDetail.ranking && <div className={styles.waterfallRanking}>NO.{currentImgDetail.ranking}</div>}
                            <div className={styles.waterfallNumber}>{currentImgDetail.number}</div>
                            <div className={styles.waterfallVoteCount}>{currentImgDetail.voteCount}票</div>
                        </div>     
                    </div>
                    {(type != 2 && user?.userInfo?.code != currentImgDetail.user?.code) && <div className={styles.detailBtnBox}>
                        <div className={classNames(styles.canvass, photographyDetail.status == 2 ? styles.canvassGray : '')} onClick={() => this.canvassForVotes()}>为TA拉票</div>
                        <div className={classNames(styles.openAppVote, photographyDetail.status == 2 ? styles.openAppVoteGray : '')}>打开App端投票</div>
                    </div>}
                    {(type == 2 && user?.userInfo?.code == currentImgDetail.user?.code) && <>
                        {currentImgDetail.status == 1 &&<div className={classNames(styles.mycanvass, photographyDetail.status == 2 ? styles.mycanvassGray : '')} onClick={() => this.canvassForVotes()}>我要拉票</div>}
                        {currentImgDetail.status == 0 && <div className={classNames(styles.waterfallStatus)}>
                            <img 
                                src={`${BASE_IMG_URL}/photography/icon6.png`} 
                                alt="审核中"
                                className={styles.icon}
                            />审核中
                        </div>}
                        {currentImgDetail.status == 2 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                            <img 
                                src={`${BASE_IMG_URL}/photography/icon7.png`} 
                                alt="审核中"
                                className={styles.icon}
                            />
                            审核未通过
                        </div>}
                        {currentImgDetail.status == 3 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                            已下架
                            <img 
                                src={`${BASE_IMG_URL}/photography/icon8.png`} 
                                alt="审核中"
                                className={styles.icon}
                            />
                        </div>}
                    </>}
                </div>} */}

                {/* 加载更多 */}
                {hasMore && (
                    <div className={styles.loadMoreContainer}>
                        {loading ? (
                            <div className={styles.loadingText}>加载中...</div>
                        ) : (
                            <button 
                                className={styles.loadingText}
                                onClick={onLoadMore}
                            >
                                加载更多
                            </button>
                        )}
                    </div>
                )}
                
                {!hasMore && data.length > 0 && (
                    // <div className={styles.noMoreText}>没有更多数据了</div>
                    <div className={styles.noMoreText}></div>
                )}
            </div>
        );
    }

    render() {
        return this.renderContent();
    }
}

export default withRouter(H5WaterfallFlow);
